import { useMemo, useState } from 'react';
import { useSession } from '../../auth/SessionProvider';
import { saveTask, useBoard, useClients, useTasks } from '../../lib/data';
import { dayDiff } from '../../lib/format';
import { PRIORITIES, TASK_STAGES, type Client, type Task, type TaskStage } from '../../lib/types';
import { useLocalState } from '../../lib/useLocalState';
import { Icon } from '../../ui/icons';
import { ErrorBox, OwnerOptions, useDrawer, useToast } from '../../ui/kit';
import { TaskLine } from './TaskLine';

type View = 'list' | 'clients' | 'board';
type Due = 'all' | 'overdue' | 'today' | 'week';

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 } as const;

export function Tasks() {
  const { me, can } = useSession();
  const { open } = useDrawer();
  const tasks = useTasks();
  const clients = useClients();
  const board = useBoard(can('pipeline'));

  const [view, setView] = useLocalState<View>('tasks.view', 'list');
  const [scope, setScope] = useLocalState('tasks.scope', 'me');
  const [clientF, setClientF] = useLocalState('tasks.client', 'all');
  const [prioF, setPrioF] = useLocalState('tasks.priority', 'all');
  const [due, setDue] = useLocalState<Due>('tasks.due', 'all');
  const [q, setQ] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [sort, setSort] = useLocalState<'due' | 'priority'>('tasks.sort', 'due');

  const leads = useMemo(() => new Map((board.data ?? []).map((l) => [l.id, l])), [board.data]);
  const byClient = useMemo(
    () => new Map((clients.data ?? []).map((c) => [c.id, c])),
    [clients.data],
  );

  const now = new Date();
  const weekEnd = new Date(now.getTime() + 7 * 86_400_000);
  const filtered = (tasks.data ?? [])
    .filter((t) =>
      scope === 'all'
        ? true
        : scope === 'shared'
          ? Boolean(t.co_owner_id)
          : t.owner_id === (scope === 'me' ? me?.id : scope) ||
            t.co_owner_id === (scope === 'me' ? me?.id : scope),
    )
    .filter(
      (t) => clientF === 'all' || (clientF === 'none' ? !t.client_id : t.client_id === clientF),
    )
    .filter((t) => prioF === 'all' || t.priority === prioF)
    .filter((t) => {
      if (due === 'all') return true;
      const d = new Date(t.due_at);
      if (due === 'overdue') return !t.done_at && d < now;
      if (due === 'today') return dayDiff(t.due_at) === 0;
      return d <= weekEnd;
    })
    .filter(
      (t) =>
        !q.trim() || `${t.text} ${t.notes ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()),
    )
    .sort((a, b) =>
      sort === 'priority'
        ? PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] ||
          a.due_at.localeCompare(b.due_at)
        : a.due_at.localeCompare(b.due_at),
    );
  const openTasks = filtered.filter((t) => !t.done_at);
  const doneTasks = filtered.filter((t) => t.done_at);
  const overdue = openTasks.filter((t) => new Date(t.due_at) < now).length;

  const line = (t: Task) => (
    <TaskLine
      key={t.id}
      task={t}
      client={t.client_id ? byClient.get(t.client_id) : undefined}
      lead={t.lead_id ? leads.get(t.lead_id) : undefined}
    />
  );

  return (
    <>
      <div className="head">
        <div>
          <h1>
            <span className="grad">Tasks</span>
          </h1>
          <p>
            {openTasks.length} open{overdue ? `, ${overdue} overdue` : ''}. Yours stay private
            unless you assign them or file them under a shared client.
          </p>
        </div>
        <div className="head-r" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => open({ type: 'clients' })}>
            Clients
          </button>
          <button className="btn primary big" onClick={() => open({ type: 'task' })}>
            New task <Icon name="arrow" />
          </button>
        </div>
      </div>

      <QuickAdd
        clients={clients.data ?? []}
        defaultClient={clientF !== 'all' && clientF !== 'none' ? clientF : ''}
      />

      <div className="tbar">
        <div className="seg" role="group" aria-label="View">
          {(
            [
              ['list', 'List'],
              ['clients', 'By client'],
              ['board', 'Board'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              className={view === id ? 'on' : ''}
              aria-pressed={view === id}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <select aria-label="Whose tasks" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="me">Mine</option>
          <option value="all">Everything I can see</option>
          <option value="shared">Shared, two people</option>
          <OwnerOptions />
        </select>
        <select aria-label="Client" value={clientF} onChange={(e) => setClientF(e.target.value)}>
          <option value="all">All clients</option>
          <option value="none">No client</option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select aria-label="Priority" value={prioF} onChange={(e) => setPrioF(e.target.value)}>
          <option value="all">Any priority</option>
          {PRIORITIES.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        <select aria-label="Deadline" value={due} onChange={(e) => setDue(e.target.value as Due)}>
          <option value="all">Any deadline</option>
          <option value="overdue">Overdue</option>
          <option value="today">Today</option>
          <option value="week">Next 7 days</option>
        </select>
        <select
          aria-label="Sort"
          value={sort}
          onChange={(e) => setSort(e.target.value as 'due' | 'priority')}
        >
          <option value="due">By deadline</option>
          <option value="priority">By priority</option>
        </select>
        <input
          type="search"
          aria-label="Search tasks"
          placeholder="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <ErrorBox error={tasks.error ?? clients.error} />
      {tasks.isLoading ? (
        <section className="card sec loading">Loading…</section>
      ) : view === 'board' ? (
        <Board tasks={filtered} line={line} />
      ) : view === 'clients' ? (
        <ByClient tasks={openTasks} clients={clients.data ?? []} line={line} />
      ) : (
        <section className="card sec">
          <div className="list-pad">
            {openTasks.length ? (
              openTasks.map(line)
            ) : (
              <div className="empty">Nothing open here.</div>
            )}
          </div>
        </section>
      )}

      {view !== 'board' && (
        <>
          <button className="btn" style={{ marginTop: 6 }} onClick={() => setShowDone(!showDone)}>
            {showDone ? 'Hide' : 'Show'} completed ({doneTasks.length})
          </button>
          {showDone && (
            <section className="card sec" style={{ marginTop: 16 }}>
              <div className="list-pad">
                {doneTasks.length ? doneTasks.map(line) : <div className="empty">None yet.</div>}
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}

function QuickAdd({ clients, defaultClient }: { clients: Client[]; defaultClient: string }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [client, setClient] = useState(defaultClient);
  const [busy, setBusy] = useState(false);
  async function add() {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await saveTask(null, { text: text.trim(), client_id: client || null });
      setText('');
      toast('Task added');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card quickadd">
      <input
        type="text"
        aria-label="New task"
        placeholder="Add a task and press Enter"
        value={text}
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void add()}
      />
      <select
        aria-label="Client for the new task"
        value={client}
        onChange={(e) => setClient(e.target.value)}
      >
        <option value="">No client</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <button className="btn primary" disabled={busy || !text.trim()} onClick={() => void add()}>
        Add
      </button>
    </div>
  );
}

function ByClient({
  tasks,
  clients,
  line,
}: {
  tasks: Task[];
  clients: Client[];
  line: (t: Task) => React.ReactNode;
}) {
  const { open } = useDrawer();
  const now = new Date();
  const groups = [
    ...clients.map((c) => ({ c, list: tasks.filter((t) => t.client_id === c.id) })),
    { c: null, list: tasks.filter((t) => !t.client_id) },
  ].filter((g) => g.list.length);
  if (!groups.length) return <section className="card sec empty">Nothing open here.</section>;
  return (
    <>
      {groups.map(({ c, list }) => {
        const late = list.filter((t) => new Date(t.due_at) < now).length;
        return (
          <section key={c?.id ?? 'none'} className="card sec">
            <div className="chead clhead">
              <span className="cdot" style={{ background: c?.colour ?? '#c9c6d6' }} aria-hidden />
              <b>{c?.name ?? 'No client'}</b>
              <span className="pill sm gr">{list.length} open</span>
              {late > 0 && <span className="pill sm red">{late} overdue</span>}
              <button
                className="btn sm ghost"
                style={{ marginLeft: 'auto' }}
                onClick={() => open({ type: 'task', clientId: c?.id })}
              >
                Add
              </button>
            </div>
            <div className="list-pad">{list.map(line)}</div>
          </section>
        );
      })}
    </>
  );
}

function Board({ tasks, line }: { tasks: Task[]; line: (t: Task) => React.ReactNode }) {
  const toast = useToast();
  const [over, setOver] = useState<TaskStage | null>(null);
  async function move(id: string, stage: TaskStage) {
    try {
      await saveTask(id, { stage });
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }
  return (
    <div className="tboard">
      {TASK_STAGES.map((s) => {
        const list = tasks
          .filter((t) => t.stage === s.id)
          .slice(0, s.id === 'done' ? 30 : undefined);
        return (
          <section
            key={s.id}
            className={`card tcol ${over === s.id ? 'over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(s.id);
            }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              const id = e.dataTransfer.getData('text/task');
              if (id) void move(id, s.id);
            }}
            aria-label={s.label}
          >
            <h3>
              {s.label} <span>{list.length}</span>
            </h3>
            {list.map((t) => (
              <div
                key={t.id}
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/task', t.id)}
              >
                {line(t)}
              </div>
            ))}
            {!list.length && <p className="calempty">Drop a task here</p>}
          </section>
        );
      })}
    </div>
  );
}
